import { useEffect, useRef } from 'react';
import { useStore } from '@/lib/store';
import { useActivityStore } from '@/lib/store/activity-store';
import { intervalsClient } from '@/lib/api/intervals-client';
import { runIncrementalActivitySync } from '@/services/activity-sync-service';
import { addDays, format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import { syncIntervalsData } from '@/lib/api/intervals-sync';
import { detectThresholdChanges, buildThresholdSignature } from '@/lib/api/threshold-detection';
import { ToastAction } from '@/components/ui/toast';
import type { Profile } from '@/types';

type AutoSyncGateOptions = {
    isConnected: boolean;
    hasSynced: boolean;
    isHydrating: boolean;
    activeProfileId: string | null;
    profiles: Profile[];
};

export function isIntervalsAutoSyncReady({
    isConnected,
    hasSynced,
    isHydrating,
    activeProfileId,
    profiles,
}: AutoSyncGateOptions): boolean {
    if (!isConnected || hasSynced || isHydrating) return false;

    if (!activeProfileId) return true;

    const activeProfile = profiles.find(profile => profile.id === activeProfileId);
    if (!activeProfile) return true;

    return activeProfile.isHydrated !== false;
}

/**
 * Automatically syncs the plan with Intervals.icu activities on app mount.
 * This ensures the Logbook and ATP are up to date when the user opens the app.
 * 
 * Also checks for threshold changes (FTP, LTHR, Pace) and notifies the user.
 * Toast only shows on /dashboard or /schedule when no other dialogs are active.
 */
export function useIntervalsAutoSync() {
    const { plan, setPlan, intervals, metrics, athlete, cyclingProgression, activeProfileId } = useStore();
    const profiles = useStore(state => state.profiles);
    const isHydrating = useStore(state => state.isHydrating);
    const thresholdAckSignature = useStore(state => state.preferences.thresholdAckSignature);
    const setPreferences = useStore(state => state.setPreferences);
    const mergeConflict = useStore(state => state.mergeConflict);
    const pendingSync = useStore(state => state.pendingSync);
    const { toast } = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const hasSyncedRef = useRef(false);

    useEffect(() => {
        const sync = async () => {
            // 1. Check if connected to Intervals
            const isConnected = Boolean(intervals?.accessToken && intervals?.athleteId && !intervals?.athleteId?.startsWith('local-'));
            if (!isIntervalsAutoSyncReady({
                isConnected,
                hasSynced: hasSyncedRef.current,
                isHydrating,
                activeProfileId,
                profiles,
            })) return;

            // 2. Prevent double sync in React Strict Mode or rapid remounts
            hasSyncedRef.current = true;

            try {
                // 3. Sync Activities and Matches via Service
                if (plan && plan.length > 0) {

                    // Unified Sync (Supabase + Store + Matching)
                    if (activeProfileId) {
                        await runIncrementalActivitySync(activeProfileId);
                    }

                    // Get updated activities from store to calculate plan actuals (Legacy backward compatibility)
                    // TODO: Move Plan Actuals calculation to a selector derived from ActivityStore
                    const activityStore = useActivityStore.getState();
                    const activities = Object.values(activityStore.activities);

                    // Update Plan Actuals
                    const updatedPlan = plan.map(week => {
                        const weekStartStr = week.startDate.split('T')[0];
                        const weekStart = new Date(weekStartStr + 'T00:00:00');
                        const weekEnd = addDays(weekStart, 6);
                        const weekEndStr = format(weekEnd, 'yyyy-MM-dd');

                        const weekActivities = activities.filter(a => {
                            const actDateStr = a.start_date_local.split('T')[0];
                            return actDateStr >= weekStartStr && actDateStr <= weekEndStr;
                        });

                        const actualVolume = weekActivities.reduce((sum, a) => sum + (a.distance / 1000), 0);
                        const actualTSS = weekActivities.reduce((sum, a) => sum + (a.icu_training_load || 0), 0); // IntervalsActivity type might not have this in type def? check
                        const actualDuration = weekActivities.reduce((sum, a) => sum + ((a.moving_time || 0) / 60), 0);

                        return {
                            ...week,
                            actualVolume: Math.round(actualVolume * 10) / 10,
                            actualTSS: Math.round(actualTSS),
                            actualDuration: Math.round(actualDuration)
                        };
                    });

                    setPlan(updatedPlan);
                    console.debug('[AutoSync] Synced', activities.length, 'activities via ActivityService');
                }

                // 4. Check for Threshold Changes (Automatic FTP, LTHR, Pace)
                const athleteData = await intervalsClient.getAthlete();
                if (athleteData) {
                    // Extract latest sync result using our centralized logic
                    const syncResult = syncIntervalsData(athleteData, {
                        ftp: metrics.ftp || cyclingProgression.ftp || 0,
                        lthr: metrics.runningLthrBpm || metrics.lthrBpm || 160,
                        thresholdPace: athlete.thresholdPace || 300,
                        swimCss: metrics.css || 100,
                        swimLthr: metrics.swimLthr || metrics.lthrBpm || 150
                    });

                    // Tolerance-based comparison absorbs the ±1-2 s/km drift from
                    // the m/s ⇄ s/km pace conversion round-trip.
                    const changes = detectThresholdChanges(syncResult.metrics, {
                        ftp: metrics.ftp || cyclingProgression.ftp,
                        runningLthr: metrics.runningLthrBpm || metrics.lthrBpm,
                        cyclingLthr: metrics.cyclingLthrBpm || metrics.lthrBpm,
                        thresholdPace: athlete.thresholdPace,
                    });

                    // A suggestion is only shown once per unique set of remote
                    // values. The signature persists via preferences (synced to
                    // Supabase), so neither reloads nor new devices re-prompt
                    // until Intervals.icu actually reports different thresholds.
                    const signature = buildThresholdSignature(syncResult.metrics);
                    const alreadySuggested = thresholdAckSignature === signature;

                    // STRICTER TOAST CONDITIONS:
                    // 1. Only show on core app routes (dashboard/schedule)
                    // 2. No active overlays (merge dialog, pending sync, onboarding)
                    const isAppRoute = ['/dashboard', '/schedule'].some(r => location.pathname.startsWith(r));
                    const hasActiveOverlay = mergeConflict.active || pendingSync.active;
                    const isOnboarding = location.pathname.includes('/onboarding');

                    if (changes.length > 0 && !alreadySuggested && isAppRoute && !hasActiveOverlay && !isOnboarding) {
                        console.info('[AutoSync] Threshold changes detected on Intervals.icu:', changes);
                        setPreferences({ thresholdAckSignature: signature });

                        toast({
                            title: "New Thresholds Detected",
                            description: `Intervals.icu detected changes: ${changes.join(', ')}. Update your zones to keep training precise.`,
                            duration: 10000,
                            action: (
                                <ToastAction
                                    altText="Review Changes"
                                    onClick={() => navigate('/settings?tab=zones')
                                    }
                                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                                >
                                    Review & Update
                                </ToastAction>
                            ),
                        });
                    } else if (changes.length > 0) {
                        // Log but don't show - not on an appropriate page or overlay is active
                        console.debug('[AutoSync] Threshold changes detected but suppressed (route:', location.pathname, 'overlay:', hasActiveOverlay, ')');
                    }
                }
            } catch (error) {
                console.error('[AutoSync] Failed to sync with Intervals.icu:', error);
            }
        };

        sync();
    }, [plan, setPlan, intervals, metrics, athlete, cyclingProgression, activeProfileId, profiles, isHydrating, thresholdAckSignature, setPreferences, toast, navigate, location.pathname, mergeConflict.active, pendingSync.active]);
}
